import React, {useState, useEffect, useRef} from 'react';
import Navbar from './components/Navbar';
import './App.css';
import { Routes, Route, Navigate } from 'react-router-dom';
import { isChatterboxLive } from './lib/featureFlags';
import Home from './components/pages/Home';
import Restaurant from './components/pages/Restaurant';
import Footer from './components/Footer'
import Catering from './components/pages/Catering';
import Chatterbox from './components/pages/Chatterbox';
// import FAQs from "./components/pages/FAQs";
import MarketMenuCategory from './components/market_menu/MarketMenuCategory';
import { fetchCategories, createBasket, fetchBasket, handleAddToBasket, 
  handleUpdateBasketItemQuantity, handleRemoveFromBasket, handleEmptyBasket } from './lib/ShopifyUtility';
import Checkout from './components/pages/Checkout';
import MarketMenuItemDetailedWrapper from './components/MarketMenuItemDetailedWrapper';
import Gallery from "./components/pages/Gallery";
// import Confirmation from './components/pages/Confirmation';

function App() {
  const [categories, setCategories] = useState([]);
  const [basketId, setBasketId] = useState(() => {
    // Load currentBasketId from localStorage if available
    return localStorage.getItem('basketId') || null;
  });
  const [basket, setBasket] = useState({});
  const basketRef = useRef(basket);
  basketRef.current = basket;
  const [emptyingBasket, setEmptyingBasket] = useState(false)


    const initializeBasket = React.useCallback(async () => {
        let currentBasketId = basketId;

        // Try to fetch existing basket
        if (currentBasketId) {
            const fetchedBasket = await fetchBasket(currentBasketId);

            if (fetchedBasket) {
                setBasket(fetchedBasket);
                return currentBasketId;
            }
        }

        // Create new basket if none exists or fetch failed
        const newBasket = await createBasket();
        currentBasketId = newBasket.id;
        setBasketId(currentBasketId);
        localStorage.setItem('basketId', currentBasketId);
        setBasket(newBasket);

        return currentBasketId;
    }, [basketId]);

    const withValidBasket = async (actionFn) => {
        const validBasketId = await initializeBasket();
        return await actionFn(validBasketId);
    };

    const refreshBasket = async (id) => {
        const basketData = await fetchBasket(id);
        setBasket(basketData);
    };

    const getCartQuantityForVariant = (cart, variantId) => {
        if (!cart?.lines?.edges || !variantId) return 0;
        return cart.lines.edges.reduce((sum, { node }) => {
            const id = node?.merchandise?.id;
            return id === variantId ? sum + (node.quantity ?? 0) : sum;
        }, 0);
    };

    const addToBasket = async (quantity, variantId, options = {}) => {
        return await withValidBasket(async (id) => {
            const basketBeforeAdd = basketRef.current;
            const prevVariantQty = getCartQuantityForVariant(basketBeforeAdd, variantId);
            const quantityAvailable = options.quantityAvailable;

            let quantityToAdd = quantity;
            let showStockLimitModal = false;

            if (quantityAvailable != null) {
                const wouldExceed = prevVariantQty + quantity > quantityAvailable;
                if (wouldExceed) {
                    quantityToAdd = Math.max(0, quantityAvailable - prevVariantQty);
                    showStockLimitModal = true;
                    if (quantityToAdd === 0) {
                        return { success: false, showStockLimitModal: true };
                    }
                }
            }

            const result = await handleAddToBasket(id, quantityToAdd, variantId);
            if (!result) {
                return { success: false, showStockLimitModal: true };
            }
            if (result.success && result.cart) {
                setBasket(result.cart);
                const newVariantQty = getCartQuantityForVariant(result.cart, variantId);
                if (newVariantQty < prevVariantQty + quantityToAdd) {
                    showStockLimitModal = true;
                }
            } else if (result.success) {
                await refreshBasket(id);
            }
            if (!result.success) {
                return { ...result, showStockLimitModal: result.showStockLimitModal || true };
            }
            return { ...result, showStockLimitModal: result.showStockLimitModal || showStockLimitModal };
        });
    };

    const updateItemBasketQuantity = async (itemId, quantity) => {
        return await withValidBasket(async (id) => {
            const result = await handleUpdateBasketItemQuantity(id, itemId, quantity);
            if (result?.success) {
                await refreshBasket(id);
            }
            return result;
        });
    };

    const removeFromBasket = async (itemId) => {
        await withValidBasket(async (id) => {
            await handleRemoveFromBasket(id, itemId);
            await refreshBasket(id);
        });
    };

    const emptyBasket = async () => {
        setEmptyingBasket(true);
        await withValidBasket(async (id) => {
            await handleEmptyBasket(id);
            await refreshBasket(id);
        });
        setEmptyingBasket(false);
    };

    useEffect(() => {
        const setup = async () => {
            const cat = await fetchCategories();
            setCategories(cat);

            await initializeBasket(); // ensures basket is created and fetched
        };

        setup();
    }, [initializeBasket]);



    return (
      <div className='app-layout'>
        <Navbar basket={basket} emptyBasket={emptyBasket} removeFromBasket={removeFromBasket} updateItemBasketQuantity={updateItemBasketQuantity} emptyingBasket={emptyingBasket} getCartQuantityForVariant={getCartQuantityForVariant}/>
        <main className='app-main'>
        <Routes>
        <Route path='/' element={<Home />} />
        <Route path='restaurant' element={<Restaurant />} />
        <Route path='catering' element={<Catering />} />
        <Route path='gallery' element={<Gallery/>} />
        {isChatterboxLive ? (
          <>
            <Route path='chatterbox' element={<Chatterbox addToBasket={addToBasket} />} />
            {categories?.map((cat) => (
              <Route
                key={cat.id}
                path={'chatterbox/' + cat.handle}
                element={
                  <MarketMenuCategory
                    catHandle={cat.handle}
                    catName={cat.title.toUpperCase()}
                    categories={categories}
                    onAddToBasket={addToBasket}
                    basket={basket}
                    getCartQuantityForVariant={getCartQuantityForVariant}
                  />
                }
              />
            ))}
            <Route
              path='chatterbox/:catHandle/:itemHandle/*'
              element={<MarketMenuItemDetailedWrapper addToBasket={addToBasket} basket={basket} getCartQuantityForVariant={getCartQuantityForVariant} />}
            />
          </>
        ) : (
          <Route path='chatterbox/*' element={<Navigate to='/' replace />} />
        )}
        {isChatterboxLive ? (
          <Route
            path='checkout'
            element={
              <Checkout
                basket={basket}
                removeFromBasket={removeFromBasket}
                updateItemBasketQuantity={updateItemBasketQuantity}
                getCartQuantityForVariant={getCartQuantityForVariant}
              />
            }
          />
        ) : (
          <Route path='checkout' element={<Navigate to='/' replace />} />
        )}
        {/*<Route*/}
        {/*  path='checkout/confirmation'*/}
        {/*  element={*/}
        {/*    <Confirmation*/}
        {/*      order={order}*/}
        {/*    />*/}
        {/*  }*/}
        {/*/>*/}
        {/*<Route path='chatterbox/faqs' element={<FAQs/>}/>*/}
      </Routes>
        </main>
      <Footer />
    </div>
  );
}

export default App;






